import { useState, useCallback, useMemo, useEffect } from 'react';

interface UseManagedGraphsOptions {
  graphs: any[];
  initialEnabled?: Record<string, string[]>;
  dataLayerKey: string;
}

/**
 * Hook to manage graph selection state and reset functionality
 * Provides a consistent interface for all graph types (sensors, oil, coolant, alarms)
 */
export const useManagedGraphs = ({ 
  graphs, 
  initialEnabled,
  dataLayerKey 
}: UseManagedGraphsOptions) => {
  // Initialize selected sections from initial enabled or all graphs
  const [selectedSections, setSelectedSections] = useState<string[]>(() => {
    if (initialEnabled && initialEnabled[dataLayerKey]) {
      return initialEnabled[dataLayerKey];
    }
    return graphs.map((g) => g.id);
  });

  const [triggerReset, setTriggerReset] = useState(false);
  const [loadingState, setLoadingState] = useState(false);

  // Update selected sections when initial enabled changes
  // Commented out to prevent infinite re-render loop
  // This was causing state updates in a loop
  // useEffect(() => {
  //   if (initialEnabled && initialEnabled[dataLayerKey]) {
  //     setSelectedSections(initialEnabled[dataLayerKey]);
  //   }
  // }, [initialEnabled, dataLayerKey]);

  // Generate enabled graphs mapping
  // Use JSON.stringify to create a stable dependency
  const enabledGraphs = useMemo(
    () => Object.fromEntries(graphs.map((el: any) => [el.id, selectedSections.includes(el.id)])),
    [JSON.stringify(graphs.map(g => g.id)), selectedSections]
  );

  // Handle selection change
  const handleSelectionChange = useCallback((selected: string[]) => {
    setSelectedSections(selected);
  }, []);

  // Handle reset trigger
  const handleReset = useCallback(() => {
    setTriggerReset(true);
  }, []);

  // Handle data layer reset
  const handleDataLayerReset = useCallback(() => {
    if (initialEnabled && initialEnabled[dataLayerKey]) {
      setSelectedSections(initialEnabled[dataLayerKey]);
    } else {
      // Reset to all graphs if no initial configuration
      // Use a stable reference to avoid re-creating arrays
      setSelectedSections((prev) => graphs.map((g) => g.id));
    }
    setTriggerReset(false);
  }, [JSON.stringify(graphs.map(g => g.id)), initialEnabled, dataLayerKey]);

  // Handle loading state change
  const handleLoadingStateChange = useCallback((isLoading: boolean) => {
    setLoadingState(isLoading);
  }, []);

  return {
    selectedSections,
    setSelectedSections,
    enabledGraphs,
    triggerReset,
    setTriggerReset,
    loadingState,
    handleSelectionChange,
    handleReset,
    handleDataLayerReset,
    handleLoadingStateChange,
  };
};